// DynamicTable.jsx
import React from "react";
import { MoreHorizontal } from "lucide-react";
import SortableHeader from "./SortableHeader";

const DynamicTable = ({
  columns = [],
  data = [],
  sortField,
  sortOrder,
  handleSort,
  onRowClick,
  onActionClick,
  noImage,
  selectedIds = [],
  dropdownOpenId,
  suppressHighlight = false,
  buttonRefs,
  rowKey = "id",

  searchTerm,
  selectedStatuses = [],
  selectedParentBrands = [],
  emptySearchImg,
  noBrandImage,
  emptyImage,
  emptyTitle,
  emptyMessage,
  showActions = true,
  showCheckbox = false,
  toggleSelect,
  toggleAll,



}) => {

  // 🔹 Check if table is empty
  const isEmpty = data.length === 0;

  // 🔹 Determine if filters are applied
  const filtersApplied =
    searchTerm ||
    (selectedStatuses?.length > 0) ||
    (selectedParentBrands?.length > 0);

  // 🔹 If empty → show empty-state UI and STOP rendering table
  if (isEmpty) {
    return (
      <div className="empty-state">
        <div className="empty-state-content">
          <div className="icon">
            <img
              src={emptyImage}
              alt="empty"
              style={{ width: 120, height: 120 }}
            />
          </div>

          <h1 className="no-item-title mt-2">{emptyTitle}</h1>

          <p className="no-item-text">{emptyMessage}</p>

        </div>
      </div>
    );
  }


  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <table
        className="entities-table"
        style={{
          width: "100%",
          tableLayout: "fixed",
          borderCollapse: "collapse",
          minWidth: "900px",
        }}
      >

        {/* ---------- HEADER ---------- */}
        <thead>
          <tr>
            {/* CHECKBOX HEADER (optional) */}
            {showCheckbox && (
              <th
                style={{
                  width: 50,
                  position: "sticky",
                  left: 0,
                  zIndex: 5,
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    data.length > 0 &&
                    selectedIds.length === data.length
                  }
                  onChange={toggleAll}
                />
              </th>
            )}
            {columns.map((col, index) => {
              const isFirst = index === 0;

              return col.sortable ? (
                <th
                  key={col.field}
                  style={{
                    height: "45px",
                    padding: "0px",
                    verticalAlign: "middle",
                    alignSelf: 'stretch',
                    alignItems: 'center',
                    gap: '12px',
                    minWidth: col.width || "150px",
                    width: col.width || "150px",


                    ...(isFirst && {
                      position: "sticky",
                      left: 0,
                      zIndex: 4,
                    })
                  }}
                >
                  <SortableHeader
                    label={col.label}
                    field={col.field}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    handleSort={handleSort}
                  />
                </th>
              ) : (
                <th
                  key={col.field}
                  style={{
                    ...(isFirst && {
                      position: "sticky",
                      left: 0,
                      // background: "#F1F3F5",
                      zIndex: 4,
                    })
                  }}
                >
                  {col.label}
                </th>
              );
            })}

            {/* ACTION HEADER */}
            {showActions && (
              <th
                className="actionHeader"
                style={{
                  position: "sticky",
                  right: 0,
                  // background: "#F1F3F5",
                  textAlign: "center",
                  zIndex: 4,
                }}
              >
                Action
              </th>
            )}
          </tr>
        </thead>


        {/* ---------- BODY ---------- */}
        <tbody>
          {data.map((row, idx) => {

            const rowBg = idx % 2 ? "#F8F9FA" : "#FFFFFF";

            const isRowSelected =
              (selectedIds.includes(row[rowKey]) ||
                dropdownOpenId === row[rowKey]) &&
              !suppressHighlight;

            return (
              <tr
                key={row[rowKey] || idx}
                onClick={() => {
                  console.log("Row clicked", row);
                  onRowClick && onRowClick(row);
                }}

                className={[
                  idx % 2 ? "striped" : "",
                  isRowSelected ? "row-selected" : "",
                ].join(" ")}
                style={{
                  height: "45px",
                  cursor: onRowClick ? "pointer" : "default",
                  background: rowBg,
                }}
              >
                {/* ROW CHECKBOX (optional) */}
                {showCheckbox && (
                  <td
                    style={{
                      width: 50,
                      position: "sticky",
                      left: 0,
                      background: rowBg,
                      zIndex: 3,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row[rowKey])}
                      onChange={() => toggleSelect(row[rowKey])}
                    />
                  </td>
                )}

                {columns.map((col, index) => {
                  const isFirst = index === 0;
                  const value = row[col.field];

                  const stickyBase = isFirst
                    ? {
                      position: "sticky",
                      left: 0,
                      zIndex: 3,
                    }
                    : {};

                  const maskLayer = isFirst ? (
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: "100%",
                        height: "100%",
                        background: rowBg,
                        zIndex: -1,
                        pointerEvents: "none",
                      }}
                    />
                  ) : null;

                  // STATUS FIELD
                  if (col.field === "status" || col.field === "status_text") {
                    return (
                      <td
                        key={col.field}
                        style={{
                          position: "relative",
                          ...stickyBase,
                        }}
                      >
                        {maskLayer}
                        <span className={`status ${String(value || '').toLowerCase()}`}>
                          {value || "NA"}
                        </span>
                      </td>
                    );
                  }
                  // TYPE FIELD
                  if (col.field === "os_type" || col.field === "color" || col.field ==="ram" || col.field ==="storage") {
                    return (
                      <td
                        key={col.field}
                        style={{
                          position: "relative",
                          ...stickyBase,
                        }}
                      >
                        <span
                          style={{
                            backgroundColor: "#FFEB00",
                            borderRadius: "99px",
                            padding: "5px 10px",
                            fontWeight: 600,
                            fontSize: "12px",
                            color: "#212529",
                            display: "inline-block",
                            whiteSpace: "nowrap",
                            textAlign: "center",
                          }}
                        >
                          {value || "NA"}
                        </span>
                      </td>
                    );
                  }


                  // NORMAL CELL
                  return (
                    <td
                      key={col.field}
                      className={isFirst ? "firstColumn" : ""}
                      style={{
                        maxWidth: col.maxWidth || "150px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        position: "relative",
                        ...stickyBase,
                      }}
                    >
                      {maskLayer}

                      {col.render
                        ? col.render(row)
                        : value || <span style={{ color: "#ADB5BD" }}>NA</span>}
                    </td>
                  );
                })}

                {/* ACTION CELL */}
                {showActions && (
                  <td
                    style={{
                      textAlign: "center",
                      position: "sticky",
                      right: 0,
                      background: rowBg,
                      zIndex: 3,
                    }}

                    className="actionColumn"
                  >
                    <button
                      ref={(el) => buttonRefs && (buttonRefs.current[row[rowKey]] = el)}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        padding: 4,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onActionClick && onActionClick(row[rowKey])
                      }}
                    >
                      <MoreHorizontal size={20} />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>

      </table>
    </div>
  );
};

export default DynamicTable;